import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight, Activity, ChevronDown } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import { titres } from "./data";

const HeroSection = () => {
    const navigate = useNavigate();
    const [index, setIndex] = useState(0);

    // Animation des titres rotatifs
    useEffect(() => {
        const interval = setInterval(() => {
            setIndex((prev) => (prev + 1) % titres.length);
        }, 2200);
        return () => clearInterval(interval);
    }, []);

    // Stagger Effect pour l'entrée des éléments
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
                delayChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
    };

    return (
        <section className="relative w-full h-[100dvh] overflow-hidden flex items-center justify-center">
            {/* <section className="relative w-full h-[100dvh] overflow-hidden bg-[#020617] flex items-center justify-center"></section> */}

            {/* --- BACKGROUND ELEMENTS (PRESERVED & OPTIMIZED) --- */}
            <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">

                {/* Circles / Orbs (Fluidified) */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 0.12, scale: 1 }}
                    transition={{ duration: 3, ease: "easeOut" }}
                    className="absolute -top-1/2 -left-1/4 w-[1200px] h-[1200px] rounded-full bg-gradient-to-br from-blue-900/40 to-black/30 blur-3xl"
                />

                <motion.div
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 0.15, scale: 1 }}
                    transition={{ duration: 3, delay: 0.5, ease: "easeOut" }}
                    className="absolute top-1/4 -right-1/4 w-[1500px] h-[1500px] rounded-full bg-gradient-to-bl from-black/40 to-blue-950/40 blur-3xl"
                />

                {/* Pulsing Animation for Circles (Slower for premium feel) */}
                <motion.div
                    animate={{ scale: [1, 1.05, 1], opacity: [0.1, 0.15, 0.1] }}
                    transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute -bottom-1/2 left-1/4 w-[1800px] h-[1800px] rounded-full bg-gradient-to-tr from-cyan-900/20 to-blue-800/10 blur-3xl"
                />

                {/* GitHub-Style Animated Clouds (PRESERVED) */}
                {/* Left Cloud Group */}
                <motion.div
                    className="absolute left-0 top-1/4 w-80 h-48 opacity-90 z-10"
                    animate={{ x: [-20, 20, -20], y: [-10, 10, -10] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
                >
                    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-[0_0_15px_rgba(165,180,252,0.3)]">
                        <ellipse cx="50" cy="70" rx="40" ry="30" fill="url(#cloudGradient1)" />
                        <ellipse cx="90" cy="60" rx="50" ry="35" fill="url(#cloudGradient1)" />
                        <ellipse cx="140" cy="70" rx="45" ry="32" fill="url(#cloudGradient1)" />
                        <ellipse cx="110" cy="85" rx="55" ry="25" fill="url(#cloudGradient1)" />
                        <defs>
                            <linearGradient id="cloudGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#c7d2fe" stopOpacity="0.8" />
                                <stop offset="100%" stopColor="#818cf8" stopOpacity="0.4" />
                            </linearGradient>
                        </defs>
                    </svg>
                </motion.div>

                {/* Right Cloud Group */}
                <motion.div
                    className="absolute right-0 top-1/3 w-96 h-56 opacity-80 z-10"
                    animate={{ x: [20, -20, 20], y: [10, -10, 10] }}
                    transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
                >
                    <svg viewBox="0 0 240 140" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-[0_0_15px_rgba(196,181,253,0.3)]">
                        <ellipse cx="60" cy="75" rx="45" ry="32" fill="url(#cloudGradient2)" />
                        <ellipse cx="110" cy="65" rx="55" ry="38" fill="url(#cloudGradient2)" />
                        <ellipse cx="170" cy="75" rx="50" ry="35" fill="url(#cloudGradient2)" />
                        <ellipse cx="130" cy="92" rx="60" ry="28" fill="url(#cloudGradient2)" />
                        <defs>
                            <linearGradient id="cloudGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#ddd6fe" stopOpacity="0.7" />
                                <stop offset="100%" stopColor="#a78bfa" stopOpacity="0.3" />
                            </linearGradient>
                        </defs>
                    </svg>
                </motion.div>

                {/* Particles/Stars (PRESERVED) */}
                <div className="absolute inset-0">
                    {[...Array(20)].map((_, i) => (
                        <motion.div
                            key={i}
                            animate={{
                                y: [0, -100, 0],
                                x: [0, Math.sin(i) * 50, 0],
                                opacity: [0.1, 0.4, 0.1],
                            }}
                            transition={{
                                duration: 10 + i,
                                repeat: Infinity,
                                delay: i * 0.5,
                                ease: "linear"
                            }}
                            className="absolute w-1 h-1 bg-blue-300 rounded-full blur-[1px]"
                            style={{
                                left: `${Math.random() * 100}%`,
                                top: `${Math.random() * 100}%`,
                            }}
                        />
                    ))}
                </div>
            </div>

            {/* --- MAIN CONTENT (PREMIUM LAYOUT & TYPOGRAPHY) --- */}
            <motion.div
                className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 flex flex-col items-center text-center h-full justify-center"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >

                {/* 1. BADGE PREMIUM */}
                <motion.div variants={itemVariants} className="mb-8 md:mb-10">
                    <div className="group relative inline-flex items-center gap-2 px-4 py-2 md:px-5 md:py-2.5 rounded-full bg-white/[0.03] border border-white/[0.08] backdrop-blur-md shadow-[0_0_20px_rgba(59,130,246,0.1)] hover:border-blue-500/30 hover:bg-white/[0.05] transition-all duration-300">
                        <div className="relative flex h-2.5 w-2.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500"></span>
                        </div>
                        <span className="text-blue-300 text-xs md:text-sm font-medium tracking-wider uppercase">Solutions Digitales Premium</span>
                    </div>
                </motion.div>

                {/* 2. TITRES PRINCIPAUX (BILLBOARD EFFECT) */}
                <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl xl:text-9xl font-bold text-white tracking-tight leading-[1.1] mb-6 md:mb-10 max-w-6xl mx-auto">
                    <span className="block text-gray-300 font-medium text-lg sm:text-2xl md:text-3xl lg:text-4xl leading-normal mb-2 md:mb-4 tracking-wide">
                        Optez pour des solutions technologiques qui renforcent votre
                    </span>

                    {/* Zone de texte rotatif stable et centrée */}
                    <div className="h-[1.3em] min-w-[5.3em] md:h-[1.2em] relative flex justify-center items-center overflow-visible mx-auto">
                        <AnimatePresence mode="wait">
                            <motion.span
                                key={titres[index]}
                                initial={{ y: 60, opacity: 0, filter: "blur(12px)" }}
                                animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
                                exit={{ y: -60, opacity: 0, filter: "blur(12px)" }}
                                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                                className="absolute inset-0 w-full text-center text-[48px] sm:text-[70px] md:text-[100px] lg:text-[120px] bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-500 flex items-center justify-center whitespace-nowrap"
                            >
                                {titres[index]}
                            </motion.span>
                        </AnimatePresence>
                    </div>
                </motion.h1>

                {/* 3. DESCRIPTION OPTIMISÉE */}
                <motion.p variants={itemVariants} className="text-lg sm:text-lg md:text-2xl text-gray-400 mb-8 md:mb-16 max-w-3xl mx-auto leading-relaxed font-light px-4 sm:px-0">
                    et transforment votre vision en réalité digitale exceptionnelle.
                </motion.p>

                {/* 4. BOUTONS D'ACTION (CTA) */}
                <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-[85%] sm:w-auto">

                    <button
                        onClick={() => navigate("/Services")}
                        className="w-[180px] sm:w-[240px] group relative cursor-pointer px-6 py-3 sm:px-8 sm:py-5 bg-blue-600 hover:bg-blue-500 text-white text-[14px] sm:text-lg font-bold rounded-full overflow-hidden transition-all duration-300 shadow-[0_10px_40px_rgba(37,99,235,0.3)] hover:shadow-[0_20px_60px_rgba(37,99,235,0.5)] hover:-translate-y-1"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-400/0 via-white/30 to-blue-400/0 translate-x-[-150%] group-hover:translate-x-[150%] transition-transform duration-700 ease-in-out" />
                        <span className="relative flex items-center justify-center gap-2 sm:gap-3">
                            Nos Services
                            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                        </span>
                    </button>

                    <button
                        onClick={() => navigate("/Contact")}
                        className="w-[180px] sm:w-[240px] cursor-pointer px-6 py-3 sm:px-8 sm:py-5 bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-white text-center text-[14px] sm:text-lg font-medium rounded-full transition-all duration-300 backdrop-blur-md hover:border-white/30 hover:-translate-y-1 hover:shadow-lg"
                    >
                        Demander un devis
                    </button>

                </motion.div>

            </motion.div>

            {/* 5. SCROLL INDICATOR (Bottom fixed) */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5, duration: 1 }}
                className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2"
            >
                <div className="w-[1px] h-16 bg-gradient-to-b from-transparent via-blue-400 to-transparent opacity-60" />
                <motion.div
                    animate={{ y: [0, 8, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                    <ChevronDown className="w-6 h-6 text-blue-400" />
                </motion.div>
            </motion.div>

        </section>
    );
};

export default HeroSection;
